import { webDtos, webEnums } from '@vectronic/openApi-customer';

type UTCFilter = Partial<{
  utcFilterEnabled: boolean;
  utcMode: string;
  utcFrom: string;
  utcTo: string;
  lmtFrom: string;
  lmtTo: string;
  utcRecentValue: string;
  utcRecentUnit: string;
}>;

type SCTSFilter = Partial<{
  sctsFilterEnabled: boolean;
  sctsMode: string;
  sctsFrom: string;
  sctsTo: string;
  sctsLmtFrom: string;
  sctsLmtTo: string;
  sctsRecentValue: string;
  sctsRecentUnit: string;
}>;

type DTSFilter = Partial<{
  dtsFilterEnabled: boolean;
  dtsMode: string;
  dtsFrom: string;
  dtsTo: string;
  dtsLmtFrom: string;
  dtsLmtTo: string;
  dtsRecentValue: string;
  dtsRecentUnit: string;
}>;

export type FixTypeMode = 'SHOW_ALL' | 'HIDE_INVALID' | 'AT_LEAST_3D';

export type EventFilter = UTCFilter &
  SCTSFilter &
  DTSFilter &
  Partial<{
    count: number;
    lastXRecordsFilterEnabled: boolean;
    deployedOnlyFilterEnabled: boolean;
    lastReceptionFilterEnabled: boolean;
    deviceTypeFilterEnabled: boolean;
    speciesFilterEnabled: boolean;
    sexFilterEnabled: boolean;
    collarAttachedFilterEnabled: boolean;
    collarAttached: boolean;
    deviceType: string[];
    lastReceptionDate: string;
    lastReceptionUnit: string;
    lastXRecords: string;
    originFilterEnabled: boolean;
    origin: webEnums.OriginCode[];
    fixTypeFilterEnabled: boolean;
    mapFixTypeFilterEnabled: boolean;
    mapFixType: FixTypeMode;
    deviceIdsFilterEnabled: boolean;
    deviceIds: number[];
    species: string[];
    sex: string[];
    comTypeFilterEnabled: boolean;
    comType: string[];
    fixType: webEnums.FixTypeSearch[];
    mortalityStatusSingleFilterEnabled: boolean;
    mortalityStatusSingle: string;
    mortalityStatusFilterEnabled: boolean;
    mortalityStatus: string[];
    mortalityTypeFilterEnabled: boolean;
    mortalityType: webEnums.Status[];
    descriptionFilterEnabled: boolean;
    description: webEnums.ExtendedMessageType[];
    groupsFilterEnabled: boolean;
    groups: string[];
    animalNameFilterEnabled: boolean;
    animalName: string;
    invalidPositionsFilterEnabled: boolean;
    hideInvalidPositions: boolean;
    coordinatesFilterEnabled: boolean;
    minLatitude: string;
    maxLatitude: string;
    minLongitude: string;
    maxLongitude: string;
    minAltitude: string;
    maxAltitude: string;
    vfEntered: boolean;
    vfEnteredFilterEnabled: boolean;
    onlyLocalData: boolean;
    showSystemLog: boolean;
    recentUnit: string;
    networkStatusFilterEnabled: boolean;
    networkStatus: string[];
  }>;

// eslint-disable-next-line sonarjs/cognitive-complexity
export function eventFilterToRequest(
  filter: EventFilter,
  options?: { includeFixTypeMode?: boolean },
) {
  const request: webDtos.PositionRequest & {
    //desktop filters start
    onlyLocal?: boolean;
    beforeDTS?: string;
    afterDTS?: string;
    doHideSystemLogs?: boolean;
    //desktop filters end

    //collar/animal page start
    comType?: string[];
    networkStatus?: string[];
    lastReceptionDate?: string;
    lastReceptionUnit?: string;
    groups?: string[];
    deviceType?: string[];
    collarAttached?: boolean;
    mortalityStatusSingle?: string;
    sex?: string[];
    species?: string[];
    //collar/animal page end

    statusSearch?: webEnums.StatusSearch[]; //mortality events
    vfEntered?: boolean; //vf events
    fixTypeMode?: FixTypeMode; //map filters
  } = {
    showInvalidPositions: true,
    onlyLocal: false,
    page: 0,
  };
  const {
    lastXRecordsFilterEnabled,
    deployedOnlyFilterEnabled,
    lastReceptionFilterEnabled,
    deviceTypeFilterEnabled,
    groupsFilterEnabled,
    collarAttachedFilterEnabled,
    sexFilterEnabled,
    speciesFilterEnabled,
    collarAttached,
    groups,
    lastXRecords,
    utcFilterEnabled,
    sctsFilterEnabled,
    dtsFilterEnabled,
    originFilterEnabled,
    origin,
    fixTypeFilterEnabled,
    mapFixTypeFilterEnabled,
    mapFixType,
    deviceIdsFilterEnabled,
    comTypeFilterEnabled,
    networkStatusFilterEnabled,
    fixType,
    mortalityStatusFilterEnabled,
    mortalityStatus,
    mortalityStatusSingleFilterEnabled,
    mortalityStatusSingle,
    mortalityTypeFilterEnabled,
    mortalityType,
    descriptionFilterEnabled,
    description,
    hideInvalidPositions,
    count,
    coordinatesFilterEnabled,
    minLatitude,
    maxLatitude,
    minLongitude,
    maxLongitude,
    minAltitude,
    maxAltitude,
    vfEnteredFilterEnabled,
    vfEntered,
    onlyLocalData,
    showSystemLog,
    deviceIds,
    comType,
    deviceType,
    networkStatus,
    lastReceptionDate,
    lastReceptionUnit,
    species,
    sex,
  } = filter;
  if (utcFilterEnabled) applyUtcFilter(request, filter);
  if (sctsFilterEnabled) applySctsFilter(request, filter);
  if (dtsFilterEnabled) applyDtsFilter(request, filter);
  if (lastXRecordsFilterEnabled) request.lastX = +lastXRecords;
  if (originFilterEnabled && origin?.length !== 0) request.originCodes = origin;
  if (fixTypeFilterEnabled) request.fixTypeSearch = fixType;
  if (options?.includeFixTypeMode) {
    request.fixTypeMode = mapFixTypeFilterEnabled ? mapFixType : 'HIDE_INVALID';
  }
  if (deviceIdsFilterEnabled) request.deviceIds = deviceIds;
  if (deviceTypeFilterEnabled) request.deviceType = deviceType;
  if (groupsFilterEnabled) request.groups = groups;
  if (comTypeFilterEnabled) request.comType = comType;
  if (collarAttachedFilterEnabled) request.collarAttached = collarAttached;
  if (sexFilterEnabled) request.sex = sex;
  if (speciesFilterEnabled) request.species = species;
  if (lastReceptionFilterEnabled) {
    request.lastReceptionDate = lastReceptionDate;
    request.lastReceptionUnit = lastReceptionUnit;
  }
  if (networkStatusFilterEnabled) request.networkStatus = networkStatus;
  if (deployedOnlyFilterEnabled) request.deployedOnly = true;
  if (mortalityStatusSingleFilterEnabled) request.mortalityStatusSingle = mortalityStatusSingle;
  if (mortalityStatusFilterEnabled) {
    if (mortalityStatus?.includes('MORTALITY_NO_RADIUS')) {
      request.mortalityStatusCodes = [
        ...(mortalityStatus as webEnums.MortalityStatusCodes[]),
        'LOW_ACTIVITY_NO_RADIUS',
        'LOW_ACTIVITY_INSIDE_RADIUS',
        'LOW_ACTIVITY_OUTSIDE_RADIUS',
        'MORTALITY_INSIDE_RADIUS',
        'MORTALITY_OUTSIDE_RADIUS',
      ];
    } else {
      request.mortalityStatusCodes = mortalityStatus as webEnums.MortalityStatusCodes[];
    }
  }
  if (mortalityTypeFilterEnabled) {
    request.statusSearch = mortalityType;
  }
  if (vfEnteredFilterEnabled !== undefined) {
    request.vfEntered = vfEntered;
  }
  if (descriptionFilterEnabled) {
    request.extendedMessageTypes = description;
  }
  request.showInvalidPositions = !hideInvalidPositions;
  request.onlyLocal = onlyLocalData;
  request.doHideSystemLogs = !showSystemLog;
  if (count) request.count = count;
  if (coordinatesFilterEnabled) {
    request.minLatitude = inputSringToNumber(minLatitude);
    request.maxLatitude = inputSringToNumber(maxLatitude);
    request.minLongitude = inputSringToNumber(minLongitude);
    request.maxLongitude = inputSringToNumber(maxLongitude);
    request.minAltitude = inputSringToNumber(minAltitude);
    request.maxAltitude = inputSringToNumber(maxAltitude);
  }
  return request;
}

function inputSringToNumber(input: string | number) {
  if (input && input.toString().trim().length !== 0 && !Number.isNaN(+input)) return +input;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
function applyUtcFilter(request: webDtos.PositionRequest, filter: EventFilter) {
  const { utcMode, utcRecentUnit, utcFrom, utcRecentValue, utcTo } = filter;
  if (utcMode === 'time-span' || utcMode === 'time-span-lmt') {
    if (utcFrom) request.afterUTC = utcFrom;
    if (utcTo) request.beforeUTC = utcTo;
  } else if (utcMode === 'recent' && utcRecentValue) {
    if (utcRecentUnit === 'units' || utcRecentUnit === 'p') request.lastX = +utcRecentValue;
    else if (utcRecentUnit === 'h') {
      const dateHoursAgo = new Date();
      dateHoursAgo.setHours(dateHoursAgo.getHours() - +utcRecentValue, 0, 0, 0);
      request.afterUTC = dateHoursAgo.toISOString();
    } else if (utcRecentUnit === 'd') {
      const dateDaysAgo = new Date();
      dateDaysAgo.setDate(dateDaysAgo.getDate() - +utcRecentValue);
      dateDaysAgo.setHours(0, 0, 0, 0);
      request.afterUTC = dateDaysAgo.toISOString();
    } else if (utcRecentUnit === 'w') {
      const dateWeeksAgo = new Date();
      dateWeeksAgo.setDate(dateWeeksAgo.getDate() - +utcRecentValue * 7);
      dateWeeksAgo.setHours(0, 0, 0, 0);
      request.afterUTC = dateWeeksAgo.toISOString();
    } else if (utcRecentUnit === 'M') {
      const dateMonthsAgo = new Date();
      dateMonthsAgo.setMonth(dateMonthsAgo.getMonth() - +utcRecentValue);
      dateMonthsAgo.setHours(0, 0, 0, 0);
      request.afterUTC = dateMonthsAgo.toISOString();
    } else if (utcRecentUnit === 'y') {
      const dateYearsAgo = new Date();
      dateYearsAgo.setFullYear(dateYearsAgo.getFullYear() - +utcRecentValue);
      dateYearsAgo.setHours(0, 0, 0, 0);
      request.afterUTC = dateYearsAgo.toISOString();
    }
    if (utcRecentUnit !== 'units') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      request.beforeUTC = tomorrow.toISOString();
    }
  } else if (utcMode === 'last-x' && utcRecentValue) request.lastX = +utcRecentValue;
}

//NOTE same as applyUtcFilter above
// eslint-disable-next-line sonarjs/cognitive-complexity
function applySctsFilter(request: webDtos.PositionRequest, filter: EventFilter) {
  const { sctsMode, sctsRecentUnit, sctsFrom, sctsRecentValue, sctsTo } = filter;
  const DAY = 24 * 60 * 60 * 1000;
  if (sctsMode === 'time-span' || sctsMode === 'time-span-lmt') {
    if (sctsFrom) request.afterSCTS = sctsFrom;
    if (sctsTo) request.beforeSCTS = new Date(new Date(sctsTo).getTime() + DAY - 1).toISOString();
  } else if (sctsMode === 'recent' && sctsRecentValue) {
    if (sctsRecentUnit === 'units' || sctsRecentUnit === 'p') request.lastX = +sctsRecentValue;
    else if (sctsRecentUnit === 'h') {
      const dateHoursAgo = new Date();
      dateHoursAgo.setHours(dateHoursAgo.getHours() - +sctsRecentValue);
      request.afterSCTS = dateHoursAgo.toISOString();
    } else if (sctsRecentUnit === 'd') {
      const dateDaysAgo = new Date();
      dateDaysAgo.setDate(dateDaysAgo.getDate() - +sctsRecentValue);
      request.afterSCTS = dateDaysAgo.toISOString();
    } else if (sctsRecentUnit === 'w') {
      const dateWeeksAgo = new Date();
      dateWeeksAgo.setDate(dateWeeksAgo.getDate() - +sctsRecentValue * 7);
      request.afterSCTS = dateWeeksAgo.toISOString();
    } else if (sctsRecentUnit === 'M') {
      const dateMonthsAgo = new Date();
      dateMonthsAgo.setMonth(dateMonthsAgo.getMonth() - +sctsRecentValue);
      request.afterSCTS = dateMonthsAgo.toISOString();
    } else if (sctsRecentUnit === 'y') {
      const dateYearsAgo = new Date();
      dateYearsAgo.setFullYear(dateYearsAgo.getFullYear() - +sctsRecentValue);
      request.afterSCTS = dateYearsAgo.toISOString();
    }
    if (sctsRecentUnit !== 'units') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      request.beforeSCTS = tomorrow.toISOString();
    }
  }
}

//NOTE same as above
// eslint-disable-next-line sonarjs/cognitive-complexity
function applyDtsFilter(
  request: webDtos.PositionRequest & {
    beforeDTS?: string;
    afterDTS?: string;
  },
  filter: EventFilter,
) {
  const { dtsMode, dtsRecentUnit, dtsFrom, dtsRecentValue, dtsTo } = filter;
  const DAY = 24 * 60 * 60 * 1000;
  if (dtsMode === 'time-span' || dtsMode === 'time-span-lmt') {
    if (dtsFrom) request.afterDTS = dtsFrom;
    if (dtsTo) request.beforeDTS = new Date(new Date(dtsTo).getTime() + DAY - 1).toISOString();
  } else if (dtsMode === 'recent' && dtsRecentValue) {
    if (dtsRecentUnit === 'units' || dtsRecentUnit === 'p') request.lastX = +dtsRecentValue;
    else if (dtsRecentUnit === 'h') {
      const dateHoursAgo = new Date();
      dateHoursAgo.setHours(dateHoursAgo.getHours() - +dtsRecentValue);
      request.afterDTS = dateHoursAgo.toISOString();
    } else if (dtsRecentUnit === 'd') {
      const dateDaysAgo = new Date();
      dateDaysAgo.setDate(dateDaysAgo.getDate() - +dtsRecentValue);
      request.afterDTS = dateDaysAgo.toISOString();
    } else if (dtsRecentUnit === 'w') {
      const dateWeeksAgo = new Date();
      dateWeeksAgo.setDate(dateWeeksAgo.getDate() - +dtsRecentValue * 7);
      request.afterDTS = dateWeeksAgo.toISOString();
    } else if (dtsRecentUnit === 'M') {
      const dateMonthsAgo = new Date();
      dateMonthsAgo.setMonth(dateMonthsAgo.getMonth() - +dtsRecentValue);
      request.afterDTS = dateMonthsAgo.toISOString();
    } else if (dtsRecentUnit === 'y') {
      const dateYearsAgo = new Date();
      dateYearsAgo.setFullYear(dateYearsAgo.getFullYear() - +dtsRecentValue);
      request.afterDTS = dateYearsAgo.toISOString();
    }
    if (dtsRecentUnit !== 'units') {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      tomorrow.setHours(0, 0, 0, 0);
      request.beforeDTS = tomorrow.toISOString();
    }
  }
}
